"use client";

import React from "react";
import { Tag, Folder } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductConsumer() {
  const { products, loading, error, refetch } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="aspect-square rounded-lg bg-muted/40 animate-pulse" />
        <div className="space-y-6">
          <div className="h-10 w-3/4 rounded-md bg-muted/40 animate-pulse" />
          <div className="h-6 w-1/3 rounded-md bg-muted/40 animate-pulse" />
          <div className="h-24 w-full rounded-md bg-muted/40 animate-pulse" />
          <div className="h-12 w-full rounded-md bg-muted/40 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground mb-4">
          Unable to load travel package details.
        </p>
        <Button onClick={refetch} variant="outline">
          Try Again
        </Button>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Package not found.</p>
      </div>
    );
  }

  const product = products[0];
  const hasDiscount =
    product.discounted_price !== undefined &&
    product.discounted_price !== null &&
    product.discounted_price !== product.price;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
      {}
      <div className="relative">
        <div className="sticky top-24">
          <div className="aspect-square overflow-hidden rounded-lg bg-card border border-border shadow-md">
            {product.featured_image_url ? (
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-muted-foreground font-default">
                Image unavailable
              </div>
            )}
          </div>
        </div>
      </div>

      {}
      <div className="flex flex-col">
        {}
        {product.categories && product.categories.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {product.categories.map((cat, i) => (
              <Link key={cat.id} to={cat.link || "#"} data-index={i}>
                <span className="text-xs uppercase tracking-widest text-primary hover:text-secondary transition-colors font-default font-semibold">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        )}

        {}
        <h1 className="text-4xl md:text-5xl font-default font-bold text-primary tracking-tight mb-6">
          {product.title}
        </h1>

        {}
        <div className="flex items-baseline gap-3 mb-8 pb-8 border-b border-border">
          {hasDiscount ? (
            <>
              <span className="text-3xl md:text-4xl font-default font-bold text-primary">
                R{Number(product.discounted_price).toFixed(2)}
              </span>
              <span className="text-xl text-muted-foreground line-through font-default">
                R{Number(product.price).toFixed(2)}
              </span>
              <Badge variant="default" className="ml-2 bg-secondary text-secondary-foreground rounded-md">
                Special Offer
              </Badge>
            </>
          ) : (
            <span className="text-3xl md:text-4xl font-default font-bold text-primary">
              R{Number(product.price).toFixed(2)}
            </span>
          )}
        </div>

        {}
        {product.short_description && (
          <div
            className="text-base md:text-lg text-foreground leading-relaxed mb-8 font-default [&_p]:mb-3 [&_p:last-child]:mb-0"
            dangerouslySetInnerHTML={{ __html: product.short_description }}
          />
        )}

        {}
        <div className="mb-10">
          <AddToCartButton
            productId={product.id}
            variant="default"
            size="lg"
            className="w-full"
            buttonClassName="bg-primary text-white w-full h-14 text-base tracking-wide uppercase rounded-lg hover:bg-primary/90 transition-all"
            text="Book Now"
            showQuantity={true}
          />
        </div>

        {}
        {product.tags && product.tags.length > 0 && (
          <div className="flex flex-wrap items-start gap-3 pt-6 border-t border-border">
            <div className="flex items-center gap-2 text-sm text-primary font-default font-semibold">
              <Tag className="size-4" />
              <span className="uppercase tracking-widest text-xs">Highlights:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {product.tags.map((tag, i) => (
                <Link key={tag.id} to={tag.link || "#"} data-index={i}>
                  <Badge
                    variant="outline"
                    className="border-border text-foreground hover:bg-accent hover:text-accent-foreground transition-colors font-default rounded-md"
                  >
                    {tag.name}
                  </Badge>
                </Link>
              ))}
            </div>
          </div>
        )}

        {}
        {product.categories && product.categories.length > 0 && (
          <div className="flex flex-wrap items-start gap-3 pt-4">
            <div className="flex items-center gap-2 text-sm text-primary font-default font-semibold">
              <Folder className="size-4" />
              <span className="uppercase tracking-widest text-xs">
                Category:
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {product.categories.map((cat, i) => (
                <Link
                  key={cat.id}
                  to={cat.link || "#"}
                  data-index={i}
                  className="text-sm text-foreground hover:text-primary transition-colors font-default"
                >
                  {cat.name}
                  {i < product.categories.length - 1 && (
                    <span className="text-muted-foreground">,</span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

        {}
        {product.long_description && (
          <div className="mt-12 pt-12 border-t border-border">
            <h2 className="text-2xl md:text-3xl font-default font-bold text-primary mb-6 tracking-tight">
              Package Details
            </h2>
            <div
              className="text-base text-foreground leading-relaxed font-default [&_p]:mb-4 [&_p:last-child]:mb-0 [&_h2]:text-xl [&_h2]:font-default [&_h2]:font-bold [&_h2]:text-primary [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-default [&_h3]:font-bold [&_h3]:text-primary [&_h3]:mt-4 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-1 [&_strong]:text-primary [&_a]:text-primary [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: product.long_description }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProductDetails() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="2854"
      id="single-product-template-section"
      className="bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-7xl mx-auto">
        <EcommerceProductsProvider wp_query={wp_query}>
          <SingleProductConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}